import React, { useEffect } from 'react';
import moment from 'moment';
import { getRopoCompanyInvoices } from '../../../actions/getRopoDetails';
import { ropoInvoice } from '../../../types/ropo';
import InvoicesCalendar from '../../Elements/InvoicesCalendar';
import BackButton from '../../Elements/BackButton';
import RopoInvoiceDetails from '../Consumer/Invoices/RopoInvoiceDetails';
// eslint-disable-next-line
// @ts-ignore
import { themeDetails } from '../../../theme/theme.ts';

type MeteringPoint = {
  object_key: string;
  object_name: string;
  object_customerIdentifier: string;
};

type CompanyInfo = {
  identifier: string;
  customerIdentifier: string;
  customerName: string;
};

const CompanyInvoiceDetails: React.FunctionComponent = () => {
  const [invoices, setInvoices] = React.useState<ropoInvoice[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [startDate, setStartDate] = React.useState<moment.Moment | null>(moment().startOf('year'));
  const [endDate, setEndDate] = React.useState<moment.Moment | null>(moment().endOf('year'));
  const [companiesInfo, setCompaniesInfo] = React.useState<CompanyInfo[] | null>(null);
  const [selectedCompanies, setSelectedCompanies] = React.useState<string[]>([]);
  const [invoiceArray, setInvoiceArray] = React.useState<JSX.Element[]>();
  const [selectedMeteringPoints, setSelectedMeteringPoints] = React.useState<string[]>([]);
  const [uniqueMeteringPoints, setUniqueMeteringpoints] = React.useState<MeteringPoint[]>([]);
  const [isOpen, setIsOpen] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [allMeteringPointsState, setAllMeteringPointsState] = React.useState<MeteringPoint[]>([]);

  useEffect(() => {
    async function fetchCompanyInvoices(): Promise<void> {
      setLoading(true);
      setError(null);

      try {
        // Fetch invoices from API
        const companyInvoiceData = await getRopoCompanyInvoices();

        // Check that there is data and invoices
        if (companyInvoiceData && companyInvoiceData.invoices.length > 0) {
          const { invoices, companyInfo } = companyInvoiceData;
          // Sort invoices - most recent invoice first
          const sortedInvoices = [...invoices].sort((a, b) => moment(b.duedate).diff(moment(a.duedate)));
          // Get all invoices and create a single array from those
          const allMeteringPoints = sortedInvoices.flatMap(invoice =>
            invoice.calculation_periods.map(p => ({
              object_key: p.object_key,
              object_name: p.object_name,
              object_customerIdentifier: invoice.customer_number
            }))
          );
          // Get unique metering points for the check box filters - removes duplicates
          const seen = new Set();
          const uniquePoints = allMeteringPoints.filter(p => {
            if (seen.has(p.object_key)) return false;
            seen.add(p.object_key);
            return true;
          });
          setAllMeteringPointsState(uniquePoints);
          setUniqueMeteringpoints(uniquePoints);
          setInvoices(sortedInvoices);
          if (Array.isArray(companyInfo)) {
            setCompaniesInfo(companyInfo);
          } else {
            setCompaniesInfo(null);
          }
          setUniqueMeteringpoints(uniquePoints);
        } else {
          setError('Laskutietoja ei löytynyt.');
        }
      } catch (err) {
        console.error('Virhe haettaessa laskuja:', err);
        setError('Laskutietojen lataaminen epäonnistui. Yritä myöhemmin uudelleen.');
      } finally {
        setLoading(false);
      }
    }
    fetchCompanyInvoices();
  }, []);

  useEffect(() => {
    if (invoices) {
      const filteredInvoices = invoices.filter(invoice => {
        const matchesCompany =
          selectedCompanies.length === 0 ||
          selectedCompanies.some(customerIdArray => customerIdArray.includes(invoice.customer_number));

        const dueDate = moment(invoice.duedate);
        const matchesDateRange = dueDate.isSameOrAfter(startDate, 'day') && dueDate.isSameOrBefore(endDate, 'day');

        const matchesMeteringPoint =
          selectedMeteringPoints.length === 0 ||
          invoice.calculation_periods.some(p => selectedMeteringPoints.includes(p.object_key));

        return matchesCompany && matchesDateRange && matchesMeteringPoint;
      });

      const updatedInvoiceArray: JSX.Element[] = filteredInvoices.map(invoice => (
        <RopoInvoiceDetails
          key={invoice.invoice_number}
          setInvoices={setInvoices}
          invoice={invoice}
          viewType={'company'}
        />
      ));

      setInvoiceArray(updatedInvoiceArray);
    }
  }, [invoices, selectedCompanies, selectedMeteringPoints, startDate, endDate]);

  // Filters unique metering points based on selected companies in the checkbox menu.
  // Updates the metering point dropdown to show only points belonging to the selected companies.
  useEffect(() => {
    if (!allMeteringPointsState.length) return;

    // allMeteringPointsState has always all metering points (prevents overwriting uniqueMeteringpoints on filter reset)
    // If no companies are selected, show all metering points
    if (selectedCompanies.length === 0) {
      setUniqueMeteringpoints(allMeteringPointsState);
      return;
    }

    // Use .flat() here to flatten nested company id arrays
    // selectedCompanies is an array of arrays: e.g. [['123'], ['456']]
    // After .flat(): ['123', '456']
    const selectedCompanyIds = selectedCompanies.flat();

    // Filtering logic - check matching customerIndentifier values between company and meteringPoint
    const filteredMeteringPoints = allMeteringPointsState.filter(
      point => point.object_customerIdentifier && selectedCompanyIds.includes(point.object_customerIdentifier)
    );

    setUniqueMeteringpoints(filteredMeteringPoints);
  }, [selectedCompanies, allMeteringPointsState]);

  /** Handler for Company checkbox */
  const handleCheckboxChange = (customerIdentifier: string) => {
    const updated = selectedCompanies.includes(customerIdentifier)
      ? selectedCompanies.filter(id => id !== customerIdentifier)
      : [...selectedCompanies, customerIdentifier];
    setSelectedCompanies(updated);
  };
  /** Handler for Metering Point checkbox */
  const handleMeteringPointChange = (point: string) => {
    const updated = selectedMeteringPoints.includes(point)
      ? selectedMeteringPoints.filter(p => p !== point)
      : [...selectedMeteringPoints, point];
    setSelectedMeteringPoints(updated);
  };

  let invoiceTable: JSX.Element = <></>;
  invoiceTable = (
    <div className="ropo-table-responsive">
      <table>
        <tbody>
          <tr>
            <td>Eräpäivä</td>
            <td>Laskun numero</td>
            <td>Laskutyyppi</td>
            <td>Laskun summa (sis. alv)</td>
            <td>Laskun summa (alv 0%)</td>
            <td>Maksettava</td>
            <td>Laskun tila</td>
            <td>Laskun pdf</td>
          </tr>
          {invoiceArray}
        </tbody>
      </table>
    </div>
  );

  return (
    <div>
      <div className="wrapper contracts">
        {/** Back Button takes you back to the Dashboard */}
        <div className="btn-row">
          <BackButton to="/yritys/yleisnakyma" />
          <h1>Laskutiedot</h1>
        </div>
        <div className="filter-wrapper">
          {/** Calendar view to select start and end dates */}
          <InvoicesCalendar
            setStartDate={setStartDate}
            setEndDate={setEndDate}
            startDate={startDate}
            endDate={endDate}
          />
          {!loading && (
            <div className="checkbox-wrapper">
              {/* Company checkboxes */}
              <div className="company-filter">
                <strong>Valitse yhtiöt:</strong>
                <div>
                  {companiesInfo &&
                    companiesInfo.map(company => (
                      <label key={company.identifier} style={{ display: 'block', marginBottom: '4px' }}>
                        <input
                          type="checkbox"
                          value={company.customerIdentifier}
                          checked={selectedCompanies.includes(company.customerIdentifier)}
                          onChange={() => handleCheckboxChange(company.customerIdentifier)}
                        />
                        {` ${company.customerName}`}
                      </label>
                    ))}
                </div>
              </div>
              {/* Metering point checkboxes */}
              <div className="metering-point-filter">
                <strong>Valitse mittauspisteet:</strong>

                <button
                  type="button"
                  onClick={() => setIsOpen(prev => !prev)}
                  className="toggle-metering-list"
                  aria-expanded={isOpen}
                  aria-controls="metering-point-list"
                >
                  {isOpen ? 'Piilota valinnat' : 'Näytä valinnat'}
                </button>

                {isOpen && (
                  <div id="metering-point-list" className="metering-point-list">
                    {uniqueMeteringPoints.map(point => (
                      <label key={point.object_key}>
                        <input
                          type="checkbox"
                          checked={selectedMeteringPoints.includes(point.object_key)}
                          onChange={() => handleMeteringPointChange(point.object_key)}
                        />
                        <span>
                          {`${point.object_key} - ${
                            point.object_name
                              ? point.object_name
                                  .split(' ')
                                  .slice(0, 2)
                                  .join(' ')
                              : ''
                          }`}
                        </span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        {loading && <p>Haetaan laskutietoja</p>}
        {!loading && error && <p style={{ color: 'red' }}>{error}</p>}
        {!loading && invoices && invoiceTable}
      </div>

      {themeDetails.theme === 'tke' && (
        <div className="tke-invoice-support">
          <p>
            Laskun maksamiseen ja myöhästyneisiin maksuihin liittyvissä asioissa sinua palvelee kumppanimme Ropo:{' '}
            <a href="https://ropo.fi" target="_blank" rel="noopener noreferrer">
              ropo.fi
            </a>
          </p>
        </div>
      )}
    </div>
  );
};

export default CompanyInvoiceDetails;
